import React, { useState, useEffect } from "react";
import auth from "../services/authService";
import ChangePassword from "./ChangePassword";

const DashboardPageHeader = ({ dashboardData }) => {
  const [user, setUser] = useState({});
  const [showHide, setShowHide] = useState(false);

  useEffect(() => {
    const currentUser = auth.getCurrentUser();
    setUser(currentUser || {});
  }, []);

  const handleModalShowHide = () => {
    setShowHide((prev) => !prev);
  };

  const getNumberUnit = (labelValue) => {
    const sign = Math.sign(Number(labelValue));
    return Math.abs(Number(labelValue)) >= 1.0e9
      ? sign * (Math.abs(Number(labelValue)) / 1.0e9).toFixed(2) + "B"
      : Math.abs(Number(labelValue)) >= 1.0e6
        ? sign * (Math.abs(Number(labelValue)) / 1.0e6).toFixed(2) + "M"
        : Math.abs(Number(labelValue)) >= 1.0e3
          ? sign * (Math.abs(Number(labelValue)) / 1.0e3).toFixed(2) + "K"
          : Math.abs(Number(labelValue));
  };

  // Candidate type chips arrive in whatever order the API returns them; pin them
  // to the same order on every card. Unknown names keep their original position.
  const typeOrder = ["C2C", "W2", "1099"];
  const sortByType = (list) =>
    [...(list || [])].sort((a, b) => {
      const rank = (n) => {
        const i = typeOrder.findIndex((t) => String(n || "").startsWith(t));
        return i < 0 ? typeOrder.length : i;
      };
      return rank(a.name) - rank(b.name);
    });

  const renderMetricCard = ({ name, count, icon, tone, otherInfo }, index) => {
    return (
      <div className="dashboard-metric-col" key={name}>
        <div
          className={`dashboard-metric-card dashboard-metric-${tone}`}
          style={{ animationDelay: `${index * 0.05}s` }}
        >
          <div className="dashboard-metric-top">
            <div className="dashboard-metric-info-section">
              <p className="dashboard-metric-label">{name}</p>
              <div className="dashboard-metric-count">
                {getNumberUnit(count || 0)}
              </div>
            </div>
            <div className="dashboard-metric-icon">
              <i className={icon} />
            </div>
          </div>
          {otherInfo && otherInfo.length > 0 && (
            <div className="dashboard-metric-details">
              {otherInfo.map((item, key) => (
                <span className="dashboard-metric-chip" key={`${name}-${item.name}-${key}`}>
                  {item.name.replace("Basis", "")}
                  <strong>{getNumberUnit(item.count || 0)}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const dashboardHeaderStyles = `
    .dashboard-metrics-wrap {
      margin: 0 0 4px;
    }

    /* Cards flow 4-up and the trailing row grows to fill the width, so an
       odd card count never leaves a gap at the end of the last row. */
    .dashboard-metrics-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
      margin-bottom: 16px;
    }

    .dashboard-metric-col {
      flex: 1 1 calc(25% - 14px);
      min-width: 232px;
    }

    @keyframes metricCardEntrance {
      0% {
        opacity: 0;
        transform: translateY(16px) scale(0.98);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .dashboard-metric-card {
      animation: metricCardEntrance 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
      background: rgba(255, 255, 255, 0.65);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 12px;
      box-shadow:
        0 1px 2px rgba(15, 23, 42, 0.03),
        0 6px 18px rgba(15, 23, 42, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.7);
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
      padding: 14px 16px 12px;
      position: relative;
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .dashboard-metric-card:hover {
      box-shadow:
        0 2px 4px rgba(15, 23, 42, 0.04),
        0 12px 28px rgba(15, 23, 42, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.85);
      transform: translateY(-3px);
      border-color: rgba(99, 102, 241, 0.15);
    }

    .dashboard-metric-card::before {
      content: "";
      height: 3px;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
      transition: height 0.25s ease;
    }

    .dashboard-metric-card:hover::before {
      height: 4px;
    }

    .dashboard-metric-top {
      align-items: flex-start;
      display: flex;
      gap: 10px;
      justify-content: space-between;
    }

    .dashboard-metric-info-section {
      min-width: 0;
      flex: 1;
    }

    .dashboard-metric-label {
      color: #64748b;
      font-size: 11px;
      font-weight: 750;
      letter-spacing: 0.05em;
      line-height: 1.3;
      margin: 0 0 6px;
      min-height: 28px;
      text-transform: uppercase;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .dashboard-metric-count {
      color: #0f172a;
      font-size: 24px;
      font-weight: 850;
      font-variant-numeric: tabular-nums;
      letter-spacing: -0.03em;
      line-height: 1.1;
    }

    .dashboard-metric-icon {
      align-items: center;
      border-radius: 10px;
      display: flex;
      flex: 0 0 auto;
      height: 38px;
      justify-content: center;
      transition: transform 0.25s ease;
      width: 38px;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.2);
    }

    .dashboard-metric-card:hover .dashboard-metric-icon {
      transform: scale(1.06);
    }

    .dashboard-metric-icon i {
      font-size: 16px;
      line-height: 1;
      display: inline-block;
      font-family: FontAwesome !important;
    }

    .dashboard-metric-details {
      border-top: 1px solid rgba(226, 232, 240, 0.5);
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 10px;
      padding-top: 9px;
    }

    .dashboard-metric-chip {
      align-items: center;
      background: rgba(248, 250, 252, 0.75);
      border: 1px solid rgba(226, 232, 240, 0.8);
      border-radius: 999px;
      color: #475569;
      display: inline-flex;
      font-size: 10.5px;
      font-weight: 700;
      gap: 4px;
      line-height: 1;
      padding: 4px 8px;
      transition: all 0.2s ease;
    }

    .dashboard-metric-chip:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }

    .dashboard-metric-chip strong {
      color: #0f172a;
      font-size: 11px;
      font-variant-numeric: tabular-nums;
    }

    /* --- Tone variants with gradient orbs --- */
    .dashboard-metric-blue::before { background: linear-gradient(90deg, #2563eb, #60a5fa); }
    .dashboard-metric-blue .dashboard-metric-icon { background: linear-gradient(135deg, rgba(37,99,235,0.12), rgba(96,165,250,0.15)); }
    .dashboard-metric-blue .dashboard-metric-icon i { color: #1d4ed8; }

    .dashboard-metric-teal::before { background: linear-gradient(90deg, #0f766e, #2dd4bf); }
    .dashboard-metric-teal .dashboard-metric-icon { background: linear-gradient(135deg, rgba(15,118,110,0.12), rgba(45,212,191,0.15)); }
    .dashboard-metric-teal .dashboard-metric-icon i { color: #0f766e; }

    .dashboard-metric-amber::before { background: linear-gradient(90deg, #d97706, #fbbf24); }
    .dashboard-metric-amber .dashboard-metric-icon { background: linear-gradient(135deg, rgba(217,119,6,0.12), rgba(251,191,36,0.15)); }
    .dashboard-metric-amber .dashboard-metric-icon i { color: #b45309; }

    .dashboard-metric-rose::before { background: linear-gradient(90deg, #e11d48, #fb7185); }
    .dashboard-metric-rose .dashboard-metric-icon { background: linear-gradient(135deg, rgba(225,29,72,0.1), rgba(251,113,133,0.15)); }
    .dashboard-metric-rose .dashboard-metric-icon i { color: #be123c; }

    .dashboard-metric-violet::before { background: linear-gradient(90deg, #7c3aed, #a78bfa); }
    .dashboard-metric-violet .dashboard-metric-icon { background: linear-gradient(135deg, rgba(124,58,237,0.12), rgba(167,139,250,0.15)); }
    .dashboard-metric-violet .dashboard-metric-icon i { color: #6d28d9; }

    .dashboard-metric-slate::before { background: linear-gradient(90deg, #475569, #94a3b8); }
    .dashboard-metric-slate .dashboard-metric-icon { background: linear-gradient(135deg, rgba(71,85,105,0.12), rgba(148,163,184,0.15)); }
    .dashboard-metric-slate .dashboard-metric-icon i { color: #334155; }

    .dashboard-metric-cyan::before { background: linear-gradient(90deg, #0891b2, #22d3ee); }
    .dashboard-metric-cyan .dashboard-metric-icon { background: linear-gradient(135deg, rgba(8,145,178,0.12), rgba(34,211,238,0.15)); }
    .dashboard-metric-cyan .dashboard-metric-icon i { color: #0e7490; }
  `;

  const metricCards = [
    {
      name: "Candidates - Enrolled",
      count: dashboardData.allCandidate,
      otherInfo: sortByType(dashboardData.allCandidateTypes),
      icon: "fa fa-id-badge",
      tone: "blue",
    },
    {
      name: "Candidates - Activated/Billable",
      count: dashboardData.activeCandidate,
      otherInfo: sortByType(dashboardData.activeCandidateTypes),
      icon: "fa fa-check-circle-o",
      tone: "teal",
    },
    {
      name: "Candidates - Deactivated",
      count: dashboardData.inActiveCandidate,
      otherInfo: sortByType(dashboardData.inActiveCandidateTypes),
      icon: "fa fa-user-times",
      tone: "rose",
    },
    {
      name: "Subcontractors - Enrolled",
      count: dashboardData.allSubcontractors,
      icon: "fa fa-handshake-o",
      tone: "amber",
    },
    {
      name: "Recruiters - Enrolled",
      count: dashboardData.allRecruiters,
      icon: "fa fa-user-plus",
      tone: "violet",
    },
    {
      name: "Users",
      count: dashboardData.users,
      otherInfo: [
        { name: "IPCS", count: dashboardData.ipcsUsers },
        {
          name: "Subcontractor",
          count: dashboardData.subcontractorUsers,
        },
        {
          name: "Recruiter",
          count: dashboardData.recruiterUsers,
        },
        {
          name: "Candidate",
          count: dashboardData.activeCandidate,
        },
      ],
      icon: "fa fa-users",
      tone: "slate",
    },
    {
      name: "Concurrent Users",
      count: dashboardData.loggedUser,
      otherInfo: [
        { name: "IPCS", count: dashboardData.ipcsLoggedUsers },
        {
          name: "Subcontractor",
          count: dashboardData.subcontractorLoggedUsers,
        },
        {
          name: "Recruiter",
          count: dashboardData.recruiterLoggedUsers,
        },
        {
          name: "Candidate",
          count: dashboardData.loggedCandidate,
        },
      ],
      icon: "fa fa-bolt",
      tone: "cyan",
    },
  ];

  return (
    <div className="dashboard-metrics-wrap">
      <style>{dashboardHeaderStyles}</style>
      {user.type === "admin" || user.type === "workflow" ? (
        <div className="dashboard-metrics-grid">
          {metricCards.map((card, index) => renderMetricCard(card, index))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default DashboardPageHeader;
